import { offsetInTrack, presentationCard } from './character_list_2.js';

const VISUAL_SELECTORS = ['.character-list-2__card', '.character-list-2__face', '.character-list-2__portrait',
  '.character-list-2__portrait > .avatar', '.character-list-2__status-panel',
  '.character-list-2__status-panel-content', '.character-list-2__condition-icons',
  '.character-list-2__identity', '.character-list-2__name', '.character-list-2__conditions',
  '.character-list-2__condition'];
const VISUAL_PROPERTIES = ['display', 'position', 'top', 'right', 'bottom', 'left',
  'width', 'height', 'maxWidth', 'maxHeight', 'boxSizing', 'padding', 'gap',
  'translate', 'scale', 'transform', 'transformOrigin', 'opacity', 'visibility',
  'filter', 'borderColor', 'borderWidth', 'borderStyle', 'borderRadius', 'boxShadow',
  'backgroundColor', 'color', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing',
  'webkitLineClamp', 'webkitBoxOrient', 'overflowWrap', 'whiteSpace',
  'flexBasis', 'flexGrow', 'flexShrink', 'flexDirection', 'alignItems', 'alignSelf',
  'objectFit', 'objectPosition', 'zIndex', 'overflow'];
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

// The real card can accept its hidden/deleted server state immediately. This
// sanitized, inert snapshot alone finishes the visible exit above the rail.
export function captureExit(slot, track, scrollLeft) {
  const ghost = presentationCard(slot);
  ghost.className = 'character-list-2__exit';
  delete ghost.dataset.characterId;
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  for (const selector of VISUAL_SELECTORS) {
    const originals = slot.querySelectorAll(selector);
    const copies = ghost.querySelectorAll(selector);
    originals.forEach((original, index) => {
      const copy = copies[index];
      if (!copy) return;
      const style = getComputedStyle(original);
      for (const property of VISUAL_PROPERTIES) copy.style[property] = style[property];
      copy.style.transition = 'none';
      copy.style.animation = 'none';
    });
  }
  const transform = getComputedStyle(slot).transform;
  const matrix = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform) : null;
  let top = 0;
  for (let element = slot; element && element !== track; element = element.offsetParent) top += element.offsetTop;
  return { ghost, left: offsetInTrack(slot, track) + (matrix?.m41 || 0) - scrollLeft,
    top: top + (matrix?.m42 || 0), width: slot.offsetWidth, height: slot.offsetHeight };
}

export function animateExit(snapshot, track, scrollLeft) {
  const { ghost, width, height } = snapshot;
  Object.assign(ghost.style, {
    position: 'absolute', left: `${snapshot.left + scrollLeft}px`, top: `${snapshot.top}px`,
    width: `${width}px`, height: `${height}px`, margin: '0', overflow: 'visible',
    pointerEvents: 'none', zIndex: '2'
  });
  track.append(ghost);
  const animation = ghost.animate([
    { opacity: 1, width: `${width}px`, overflow: 'visible', offset: 0, easing: 'ease' },
    { opacity: 0, width: `${width}px`, overflow: 'visible', offset: 160 / 380, easing: EASING },
    { opacity: 0, width: '0px', overflow: 'hidden', offset: 1 }
  ], { duration: 380, fill: 'both' });
  let removed = false;
  const cleanup = () => {
    if (removed) return;
    removed = true;
    animation.cancel();
    ghost.remove();
  };
  animation.finished.then(cleanup, cleanup);
  return { animations: [{ element: ghost, animation }], cleanup };
}

export function animateEntry(slot) {
  const card = slot.querySelector('.character-list-2__card');
  const opacity = getComputedStyle(card).opacity;
  return [
    { element: slot, animation: slot.animate([
      // Neighbor movement already reserves the final layout footprint. Reveal
      // that space visually without interpolating the real width a second time.
      { clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)' }
    ], { duration: 260, easing: EASING, fill: 'both' }) },
    { element: card, animation: card.animate([
      { opacity: 0, offset: 0 }, { opacity: 0, offset: 100 / 260 }, { opacity, offset: 1 }
    ], { duration: 260, easing: 'ease', fill: 'both' }) }
  ];
}
